import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import type { CampaignRegistrationFormData } from '../../types';
import { graphqlApi } from '../../services/graphql';
import { Spinner } from '../common/Spinner';
import CampaignBasicInfoV2 from './sections/CampaignBasicInfoV2';
import ProductServiceInfoV2 from './sections/ProductServiceInfoV2';
import CampaignMissionV2 from './sections/CampaignMissionV2';
import LoadCampaignModal from './LoadCampaignModal';
import CampaignPreviewModal from './CampaignPreviewModal';
import { generateCampaignProposalPdf } from '../../utils/campaignProposalPdf';
import { useAuthStore } from '../../store/authStore';
import { USER_TYPES } from '../../constants/userTypes';

interface CampaignRegistrationFormProps {
  campaignId?: string;
}

const SECTIONS = [
  { id: 'section-basic', labelKey: 'registration.v2.sectionBasic', required: true },
  { id: 'section-product', labelKey: 'registration.v2.sectionProduct', required: true },
  { id: 'section-mission', labelKey: 'registration.v2.sectionMission', required: true },
] as const;

const defaultValues: any = {
  // Section 1: Campaign Basic Info
  brandName: '',
  campaignName: '',
  campaignCategory: '',
  clientBrandId: '',

  // Section 2: Product/Service Info
  representativeImages: [],
  productIntroduction: '',
  productName: '',
  providedValueDescription: '',
  provisionMethod: '',
  additionalNotes: '',
  visitAddress: '',
  visitLatitude: null,
  visitLongitude: null,
  attachments: [],

  // Section 3: Campaign Mission
  numberOfRecruits: 0,
  contentChannel: '',
  contentChannels: [] as string[],
  rewardAmount: 0,
  instagramCollaboratorId: '',
  blogInsertLink: '',
  youtubeInsertLink: '',
  publishingConditions: {
    instagram: { collaboratorId: '', conditions: [] },
    blog: { insertLink: '', conditions: [] },
    youtube: { insertLink: '', conditions: [] },
  },
  recruitmentPeriod: { startDate: '', endDate: '', alwaysRecruiting: false },
  announcementStartDate: '',
  announcementEndDate: '',
  alwaysOpening: false,
  visitStartDate: '',
  visitEndDate: '',
  uploadDeadlineDays: 7,
  discussSchedule: false,
  contentWritingRequirements: '',
  secondarySubmissions: [],
  submissionPath: '',
  secondarySubmissionGuide: '',
};

const CampaignRegistrationForm: React.FC<CampaignRegistrationFormProps> = ({ campaignId: propCampaignId }) => {
  const { t } = useTranslation('campaigns');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAgency = user?.userType === USER_TYPES.AGENCY;
  const { campaignId: paramCampaignId } = useParams<{ campaignId: string }>();
  const campaignId = propCampaignId || paramCampaignId;
  const isEditMode = !!campaignId;

  const [activeSection, setActiveSection] = useState<string>(SECTIONS[0].id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(isEditMode);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<any>({
    defaultValues,
    mode: 'onBlur',
  });

  const DRAFT_KEY = `campaign-draft-${user?.userId || 'anon'}`;

  // Save draft to localStorage
  const handleSaveDraft = () => {
    const currentData = watch();
    localStorage.setItem(DRAFT_KEY, JSON.stringify(currentData));
    toast.success(t('registration.v2.draftSaved'));
  };

  // Restore draft from localStorage on mount (only for new campaigns)
  const draftRestoredRef = useRef(false);
  useEffect(() => {
    if (isEditMode || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.brandName || parsed.campaignName || parsed.productName) {
          reset({ ...defaultValues, ...parsed });
          toast.success(t('registration.v2.draftRestored'));
        }
      } catch { /* ignore corrupt data */ }
    }
  }, []);  // Run once on mount

  // Clear draft after successful submission
  const clearDraft = () => localStorage.removeItem(DRAFT_KEY);

  // Fetch existing campaign data in edit mode
  useEffect(() => {
    if (!campaignId) return;

    const fetchCampaign = async () => {
      setIsLoadingCampaign(true);
      try {
        const response = await graphqlApi.getCampaign(campaignId);
        if (response.success && response.data) {
          const campaign = response.data as any;
          // Strip system fields and __typename, merge with defaults
          const { campaignId: _cid, hostId: _hid, createdBy: _cb, status: _s, createdAt: _ca, updatedAt: _ua, isVerified: _iv, visibility: _v, __typename: _t, ...formData } = campaign;
          const clean = JSON.parse(JSON.stringify(formData, (key, value) => {
            if (key === '__typename') return undefined;
            if (value === null) return undefined;
            return value;
          }));
          // Derive contentChannels from contentChannel if array is empty (backward compat)
          if ((!clean.contentChannels || clean.contentChannels.length === 0) && clean.contentChannel) {
            clean.contentChannels = [clean.contentChannel];
          }
          // Migrate legacy per-channel fields into publishingConditions V2
          if (!clean.publishingConditions || (!clean.publishingConditions.instagram && !clean.publishingConditions.blog && !clean.publishingConditions.youtube)) {
            clean.publishingConditions = {
              instagram: {
                collaboratorId: clean.instagramCollaboratorId || '',
                conditions: [],
              },
              blog: {
                insertLink: clean.blogInsertLink || '',
                conditions: [],
              },
              youtube: {
                insertLink: clean.youtubeInsertLink || '',
                conditions: [],
              },
            };
          }
          reset({ ...defaultValues, ...clean });
        } else {
          toast.error(t('registration.notFound') || t('messages.campaignNotFound'));
          navigate('/campaigns');
        }
      } catch (error: any) {
        console.error('Error fetching campaign for edit:', error);
        toast.error(error.message || t('registration.notFound') || t('messages.loadingError'));
        navigate('/campaigns');
      } finally {
        setIsLoadingCampaign(false);
      }
    };

    fetchCampaign();
  }, [campaignId, reset, navigate, t]);

  // IntersectionObserver to track which section is visible
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // Find the most visible section
      const visibleEntries = entries.filter((entry) => entry.isIntersecting);
      if (visibleEntries.length > 0) {
        // Pick the one with the highest intersection ratio
        const mostVisible = visibleEntries.reduce((prev, curr) =>
          curr.intersectionRatio > prev.intersectionRatio ? curr : prev
        );
        setActiveSection(mostVisible.target.id);
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '-80px 0px -50% 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    SECTIONS.forEach((section) => {
      const el = sectionRefs.current[section.id];
      if (el) {
        observer.observe(el);
      }
    });

    observers.push(observer);

    return () => {
      observers.forEach((obs) => obs.disconnect());
    };
  }, []);

  // Smooth scroll to section
  const scrollToSection = useCallback((sectionId: string) => {
    const el = sectionRefs.current[sectionId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Scroll to first error field on validation failure
  const scrollToFirstError = useCallback(() => {
    // Find the first section that has an error
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;

    // Map fields to sections
    const fieldToSection: Record<string, string> = {
      advertiserName: 'section-advertiser',
      brandName: 'section-advertiser',
      contactPersonName: 'section-advertiser',
      representativeImages: 'section-basic',
      campaignName: 'section-basic',
      campaignType: 'section-basic',
      campaignCategory: 'section-basic',
      contentTypes: 'section-basic',
      recruitmentPeriod: 'section-basic',
      numberOfRecruits: 'section-basic',
      announcementDate: 'section-basic',
      productName: 'section-product',
      introductionPageUrl: 'section-product',
      providedValueDescription: 'section-product',
      provisionMethod: 'section-product',
      publishingMode: 'section-publishing',
      selectedChannels: 'section-publishing',
      publishingConditions: 'section-publishing',
      productIntroduction: 'section-guide',
      contentWritingRequirements: 'section-guide',
      additionalNotes: 'section-guide',
      recommendedKeywords: 'section-other',
      attachments: 'section-other',
      remarks: 'section-other',
    };

    const firstErrorField = errorFields[0];
    const sectionId = fieldToSection[firstErrorField];
    if (sectionId) {
      scrollToSection(sectionId);
    }
  }, [errors, scrollToSection]);

  // Form submission handler
  const onSubmit = async (data: any) => {
    // Require at least one publishing channel. Without this, the campaign
    // has no missions for influencers to apply to.
    const selectedChannels: string[] = Array.isArray(data.contentChannels) ? data.contentChannels : [];
    if (selectedChannels.length === 0) {
      toast.error(
        t('registration.v2.errors.noChannels', {
          defaultValue: '콘텐츠 발행 채널을 1개 이상 선택해주세요.',
        }),
      );
      scrollToSection('section-mission');
      return;
    }

    // Validate that every selected channel has at least one complete
    // publishing condition. Empty conditions cause the apply modal to hide
    // the channel entirely, so we block the save here instead of shipping
    // a campaign that influencers can't apply to.
    const pc = data.publishingConditions || {};
    const missingChannels: string[] = [];
    const incompleteChannels: string[] = [];
    const channelLabelKey: Record<string, string> = {
      instagram: 'registration.v2.channels.instagram',
      blog: 'registration.v2.channels.blog',
      youtube: 'registration.v2.channels.youtube',
    };

    selectedChannels.forEach((ch) => {
      const conds = pc?.[ch]?.conditions;
      if (!Array.isArray(conds) || conds.length === 0) {
        missingChannels.push(ch);
        return;
      }
      const anyIncomplete = conds.some(
        (c: any) => !c?.item || !(Number(c?.quantity) >= 1),
      );
      if (anyIncomplete) incompleteChannels.push(ch);
    });

    if (missingChannels.length > 0 || incompleteChannels.length > 0) {
      const labels = (arr: string[]) =>
        arr.map((c) => t(channelLabelKey[c] || c)).join(', ');
      if (missingChannels.length > 0) {
        toast.error(
          t('registration.v2.errors.missingConditions', {
            defaultValue: '{{channels}} 채널의 발행 조건을 1개 이상 추가해주세요.',
            channels: labels(missingChannels),
          }),
        );
      } else {
        toast.error(
          t('registration.v2.errors.incompleteConditions', {
            defaultValue: '{{channels}} 채널의 조건 항목/수량을 모두 입력해주세요.',
            channels: labels(incompleteChannels),
          }),
        );
      }
      scrollToSection('section-mission');
      return;
    }

    setIsSubmitting(true);
    try {
      // Clean up recruitment period
      if (!data.recruitmentPeriod) {
        data.recruitmentPeriod = { startDate: '', endDate: '', alwaysRecruiting: false };
      }
      if (data.recruitmentPeriod.alwaysRecruiting == null) {
        data.recruitmentPeriod.alwaysRecruiting = false;
      }
      if (data.recruitmentPeriod.alwaysRecruiting) {
        data.recruitmentPeriod.endDate = null;
      }

      // Strip __typename and keep all values (including empty strings, 0, false)
      const cleanData = JSON.parse(JSON.stringify(data, (key, value) => {
        if (key === '__typename') return undefined;
        return value;
      }));

      if (isEditMode && campaignId) {
        const result = await graphqlApi.updateCampaign(campaignId, cleanData as any);
        if (result.success) {
          toast.success(t('registration.updateSuccess'));
          navigate(`/campaigns/${campaignId}/detail`);
        } else {
          toast.error(result.error || t('registration.updateError'));
        }
      } else {
        const result = await graphqlApi.createCampaign(cleanData as any);
        if (result.success) {
          clearDraft();
          toast.success(t('registration.createSuccess'));
          const newCampaignId = result.data?.campaignId;
          navigate(newCampaignId ? `/campaigns/${newCampaignId}/detail` : '/campaigns');
        } else {
          toast.error(result.error || t('registration.createError'));
        }
      }
    } catch (error: any) {
      toast.error(error.message || (isEditMode ? t('registration.updateError') : t('registration.createError')));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = () => {
    scrollToFirstError();
  };

  // Clone campaign data into the form
  const handleLoadCampaign = (campaign: any) => {
    const { campaignId: _id, hostId: _hid, createdBy: _cb, status: _s, createdAt: _ca, updatedAt: _ua, isVerified: _iv, visibility: _v, __typename, ...cloneData } = campaign;
    // Strip __typename and convert null to undefined so defaultValues fill in
    const clean = JSON.parse(JSON.stringify(cloneData, (key, value) => {
      if (key === '__typename') return undefined;
      if (value === null) return undefined;
      return value;
    }));
    // Derive contentChannels from contentChannel if array is empty (backward compat)
    if ((!clean.contentChannels || clean.contentChannels.length === 0) && clean.contentChannel) {
      clean.contentChannels = [clean.contentChannel];
    }
    // Merge: defaultValues provide fallbacks, clean overrides with actual data
    const merged = { ...defaultValues, ...clean };
    console.log('📋 Loading campaign data:', merged);
    reset(merged);
    toast.success(t('registration.v2.campaignLoaded'));
  };

  // Set ref for a section
  const setSectionRef = (id: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  // Show loading spinner while fetching campaign data in edit mode
  if (isLoadingCampaign) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Back to campaign list */}
      <Link
        to="/campaigns"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        {t('registration.backToList')}
      </Link>

      {/* Page Title */}
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {campaignId ? t('registration.editPageTitle') : t('registration.pageTitle')}
      </h1>

      <div className="flex gap-8">
        {/* Sidebar Navigation — Desktop (≥1024px) */}
        <nav
          className="hidden lg:block w-60 shrink-0"
          aria-label="Form sections"
        >
          <div className="sticky top-20">
            <ul className="space-y-1">
              {SECTIONS.map((section, index) => {
                const isActive = activeSection === section.id;
                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 font-semibold'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        {isActive && (
                          <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0" />
                        )}
                        <span>
                          {index + 1}. {t(section.labelKey)}
                          {!section.required && (
                            <span className="ml-1 text-xs text-gray-400">
                              ({t('registration.optional')})
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {/* Campaign action buttons */}
            <div className="mt-6 space-y-2">
              <button type="button" onClick={() => setShowLoadModal(true)}
                className="w-full px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                {t('registration.loadCampaign')}
              </button>
              <button type="button" onClick={handleSaveDraft}
                className="w-full px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                {t('registration.saveDraft')}
              </button>
              <button type="button" onClick={() => setShowPreview(true)}
                className="w-full px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                {t('registration.preview')}
              </button>
              {isAgency && (
                <button type="button"
                  onClick={() => generateCampaignProposalPdf(watch(), t)}
                  className="w-full px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  {t('registration.convertProposal')}
                </button>
              )}
              <button type="submit"
                form="campaign-registration-form"
                disabled={isSubmitting}
                className={`w-full px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                }`}>
                {isSubmitting ? t('registration.submitting') : t('registration.submitCampaign')}
              </button>
            </div>
          </div>
        </nav>

        {/* Horizontal Tab Bar — Tablet (768-1023px) */}
        <div className="hidden md:block lg:hidden w-full mb-4 sticky top-16 z-10 bg-white border-b border-gray-200 -mx-4 px-4">
          <div className="flex overflow-x-auto gap-1 py-2 scrollbar-hide">
            {SECTIONS.map((section, index) => {
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => scrollToSection(section.id)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {index + 1}. {t(section.labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <form
          id="campaign-registration-form"
          onSubmit={handleSubmit(onSubmit, onError)}
          className="flex-1 min-w-0 space-y-8"
        >
          {/* Section 1: 캠페인 기본정보 */}
          <div
            id="section-basic"
            ref={setSectionRef('section-basic')}
            className={`bg-white rounded-xl shadow-sm border p-6 ${
              activeSection === 'section-basic' ? 'border-l-4 border-l-blue-500' : 'border-gray-200'
            }`}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              1. {t('registration.v2.sectionBasic')}
            </h2>
            <CampaignBasicInfoV2 control={control} setValue={setValue} watch={watch} />
          </div>

          {/* Section 2: 제공 제품/서비스 정보 */}
          <div
            id="section-product"
            ref={setSectionRef('section-product')}
            className={`bg-white rounded-xl shadow-sm border p-6 ${
              activeSection === 'section-product' ? 'border-l-4 border-l-blue-500' : 'border-gray-200'
            }`}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              2. {t('registration.v2.sectionProduct')}
            </h2>
            <ProductServiceInfoV2 control={control} setValue={setValue} watch={watch} />
          </div>

          {/* Section 3: 캠페인 미션 */}
          <div
            id="section-mission"
            ref={setSectionRef('section-mission')}
            className={`bg-white rounded-xl shadow-sm border p-6 ${
              activeSection === 'section-mission' ? 'border-l-4 border-l-blue-500' : 'border-gray-200'
            }`}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              3. {t('registration.v2.sectionMission')}
            </h2>
            <CampaignMissionV2 control={control} watch={watch} setValue={setValue} />
          </div>

          {/* End of sections — submit via sticky sidebar buttons */}
        </form>
      </div>

      {/* Load Campaign Modal */}
      <LoadCampaignModal
        isOpen={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        onSelect={handleLoadCampaign}
      />

      {/* Preview Modal */}
      <CampaignPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        data={watch()}
      />

      {/* Mobile/Tablet sticky bottom submit bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30 flex gap-2">
        <button type="button" onClick={handleSaveDraft}
          className="flex-1 px-3 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
          {t('registration.saveDraft')}
        </button>
        <button type="submit" form="campaign-registration-form" disabled={isSubmitting}
          className={`flex-1 px-3 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${
            isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
          }`}>
          {isSubmitting ? t('registration.submitting') : t('registration.submitCampaign')}
        </button>
      </div>
    </div>
  );
};

export default CampaignRegistrationForm;
